import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { ScreenField } from "@sloe/schema";
import { notifySafeCheckpoint, useReloadGuard } from "../../lib/update-reload";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  workspaceId: string;
  screenId: string;
  fields: ScreenField[];
  title: string;
  // DESIGN-1 — optional. When provided, distinct values from records[field.key]
  // populate the select dropdown when field.options isn't set in the schema.
  existingRecords?: any[];
  // DESIGN-1 — optional. When provided, used as the Create button background.
  brandColor?: string;
};

function defaultValueForField(field: ScreenField): any {
  // ScreenField.type union doesn't include "number" today; the runtime cast
  // is defensive against loose-JSON schemas that emit it.
  if ((field.type as any) === "number" || field.type === "currency") return "";
  if (field.type === "date") return "";
  return "";
}

function isEditableField(field: ScreenField): boolean {
  // Skip auto-managed fields if they ever appear in screen.fields
  if (field.key === "id" || field.key === "created_at" || field.key === "updated_at") return false;
  return true;
}

// DESIGN-1 — Infer distinct option values for a select field from existing
// records. Used when field.options isn't populated in the schema. Returns
// case-deduped values in first-appearance order. Mirrors the optionFallback
// pattern in FieldSelectEditor (field-renderers.tsx).
function inferOptionsFromRecords(field: ScreenField, records: any[]): string[] {
  if (!records || records.length === 0) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of records) {
    const v = r?.[field.key];
    if (v === undefined || v === null || v === "") continue;
    const key = String(v).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(String(v));
  }
  return out;
}

export function CreateRecordModal({
  open,
  onClose,
  onCreated,
  workspaceId,
  screenId,
  fields,
  title,
  existingRecords,
  brandColor,
}: Props) {
  const editableFields = fields.filter(isEditableField);
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const firstInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const previousOpenRef = useRef(false);

  // Reset values whenever the modal re-opens.
  useEffect(() => {
    if (open) {
      const initial: Record<string, any> = {};
      for (const f of editableFields) initial[f.key] = defaultValueForField(f);
      setValues(initial);
      setError(null);
      setSubmitting(false);
      setCompleted(false);
      // Focus first input after the modal animates in.
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, submitting, onClose]);

  const handleChange = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const isDirty = editableFields.some((field) => {
    const value = values[field.key];
    return value !== undefined && value !== null && value !== "";
  });

  useReloadGuard({
    id: `create-record:${workspaceId}:${screenId}`,
    active: open,
    busy: open && submitting,
    dirty: open && !completed,
    label: title,
  });

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;
    if (wasOpen && !open) {
      const closedSafely = completed || (!submitting && !isDirty);
      if (closedSafely) notifySafeCheckpoint("create-record-close");
    }
  }, [open, completed, submitting, isDirty]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    // Normalize numeric fields: convert "" to null, "123" to 123.
    const data: Record<string, any> = {};
    for (const f of editableFields) {
      const raw = values[f.key];
      if ((f.type as any) === "number" || f.type === "currency") {
        if (raw === "" || raw === undefined || raw === null) {
          data[f.key] = null;
        } else {
          const n = Number(raw);
          data[f.key] = Number.isFinite(n) ? n : null;
        }
      } else {
        data[f.key] = raw === undefined ? null : raw;
      }
    }

    try {
      const res = await fetch("/api/screen-data", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: workspaceId, screen_id: screenId, data }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setCompleted(true);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Couldn't create record");
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => !submitting && onClose()}
        className="fixed inset-0 z-50 bg-black/40"
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-zinc-200">
            <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {editableFields.map((field, idx) => {
              const value = values[field.key] ?? "";
              const inputClass =
                "w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900";
              return (
                <div key={field.key} className="space-y-1">
                  <label className="block text-xs font-medium text-zinc-600">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.type === "select" ? (() => {
                    // DESIGN-1 — explicit options win; otherwise infer from records.
                    const explicit = Array.isArray((field as any).options) ? (field as any).options : [];
                    const options: string[] = explicit.length > 0
                      ? explicit
                      : inferOptionsFromRecords(field, existingRecords || []);
                    if (options.length > 0) {
                      return (
                        <select
                          ref={idx === 0 ? (firstInputRef as any) : null}
                          value={value}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          className={inputClass}
                        >
                          <option value="">— Select —</option>
                          {options.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      );
                    }
                    // No options + no inferable values → fall through to a
                    // plain text input so the user can at least type something.
                    return (
                      <input
                        ref={idx === 0 ? (firstInputRef as any) : null}
                        type="text"
                        value={value}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className={inputClass}
                        placeholder={`Enter ${field.label.toLowerCase()}…`}
                      />
                    );
                  })() : field.type === "currency" ? (
                    // DESIGN-1 — currency gets a $ prefix glyph inside the input.
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400 pointer-events-none">$</span>
                      <input
                        ref={idx === 0 ? (firstInputRef as any) : null}
                        type="number"
                        value={value}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className={`${inputClass} pl-7`}
                        placeholder="0"
                      />
                    </div>
                  ) : (field.type as any) === "number" ? (
                    <input
                      ref={idx === 0 ? (firstInputRef as any) : null}
                      type="number"
                      value={value}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className={inputClass}
                      placeholder="0"
                    />
                  ) : field.type === "date" ? (
                    <input
                      ref={idx === 0 ? (firstInputRef as any) : null}
                      type="date"
                      value={value}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      ref={idx === 0 ? (firstInputRef as any) : null}
                      type="text"
                      value={value}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className={inputClass}
                      placeholder={`Enter ${field.label.toLowerCase()}…`}
                    />
                  )}
                </div>
              );
            })}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 p-6 border-t border-zinc-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
              className={`px-4 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed text-white ${
                brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
              }`}
            >
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
